import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Attachment } from './models';
import { baseApi, rollbar } from '@centerpiece/components';
import { call, all, takeEvery } from 'redux-saga/effects';
import fileDownload from 'js-file-download';
import { toast } from 'react-toastify';

export const attachment = baseApi
  .enhanceEndpoints({ addTagTypes: ['Attachments', 'Attachment'] })
  .injectEndpoints({
    endpoints: builder => ({
      getAttachments: builder.query<Attachment[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'attachment',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'Attachment' as const,
                  id,
                })),
                'Attachments',
              ]
            : ['Attachments'],
      }),
      getAttachment: builder.query<Attachment, string>({
        query: id => ({
          url: `attachment/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Attachment', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createAttachment: builder.mutation({
        query: request => ({
          url: 'attachment',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Attachments'],
      }),
      patchAttachment: builder.mutation<Attachment, Attachment>({
        query: request => ({
          url: `attachment/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Attachment', id: arg.id },
        ],
      }),
      updateAttachment: builder.mutation<Attachment, Attachment>({
        query: request => ({
          url: `attachment/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Attachment', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            attachment.util.updateQueryData('getAttachment', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(attachment.util.invalidateTags(['Attachments']))
             */
            dispatch(attachment.util.invalidateTags(['Attachments']));
          }
        },
      }),
      deleteAttachment: builder.mutation<Attachment, number>({
        query: id => ({
          url: `attachment/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Attachment' as const, arg }, 'Attachments']
            : ['Attachments'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetAttachmentsQuery,
  useGetAttachmentQuery,
  useCreateAttachmentMutation,
  useUpdateAttachmentMutation,
  usePatchAttachmentMutation,
  useDeleteAttachmentMutation,
  usePrefetch: useAttachmentsPrefetch,
  useLazyGetAttachmentQuery,
} = attachment;
const selectAttachmentsRoot = attachment.endpoints.getAttachments.select({});
export const selectAttachments = createSelector(
  selectAttachmentsRoot,
  attachment => attachment.data || []
);

export function* attachmentSaga() {
  yield all([
    takeEvery(
      attachment.endpoints.getAttachment.matchFulfilled,
      watchAttachmentDownloadSignedUrl
    ),
  ]);
}

export function* watchAttachmentDownloadSignedUrl(action: {
  type: string;
  payload: Attachment;
  meta: any;
}) {
  try {
    const attachment = action.payload;
    if (attachment?.url && attachment?.url !== '') {
      yield call(fetchDownloadAttachment, action.payload.url, attachment.name);
    }
  } catch (error: any) {
    const file_name = action.meta.arg.originalArgs.file_path
      .split('/')
      .slice(-1)[0];
    toast.error(`Unable to download file: ${file_name}`);
    rollbar.error(`Unable to download file: ${file_name}`);
  }
}

//@ts-ignore
function* fetchDownloadAttachment(url: string, file_name: string) {
  //@ts-ignore
  const response = yield fetch(url, {
    method: 'GET',
  });
  //@ts-ignore
  const myBlob = yield response.blob();
  yield fileDownload(myBlob, file_name);
}
